import React from "react";
import { Tooltip } from "antd";
import { CloseOutlined } from "@ant-design/icons";
import { askAiTokens as t } from "./tokens";
import { Attachment } from "./types";
import { fileIconFor, formatFileSize } from "./fileUtils";

interface Props {
  attachments: Attachment[];
  onRemove: (id: string) => void;
}

const statusLabel = (a: Attachment) => {
  if (a.status === "uploading") return "Uploading…";
  if (a.status === "ready") return "Ready";
  if (a.status === "error") return a.errorMessage || "Failed";
  return null;
};

const AttachmentChips: React.FC<Props> = ({ attachments, onRemove }) => {
  if (attachments.length === 0) return null;
  return (
    <div
      style={{
        display: "flex",
        flexWrap: "wrap",
        gap: t.space.s2,
        padding: `${t.space.s2}px 0`,
      }}
    >
      {attachments.map((a) => {
        const status = statusLabel(a);
        const isError = a.status === "error";
        const isReady = a.status === "ready";
        return (
          <div
            key={a.id}
            style={{
              display: "inline-flex",
              flexDirection: "column",
              gap: 4,
              padding: `${t.space.s2}px ${t.space.s3}px`,
              background: isError ? "#fef2f2" : t.color.surface,
              border: `1px solid ${
                isError
                  ? "#fecaca"
                  : isReady
                  ? t.color.border
                  : t.color.borderStrong
              }`,
              borderRadius: t.radius.md,
              minWidth: 200,
              maxWidth: 320,
              position: "relative",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: t.space.s2,
              }}
            >
              <span style={{ fontSize: 18 }}>{fileIconFor(a.file.name)}</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    fontSize: t.font.size.sm,
                    fontWeight: 500,
                    color: t.color.text,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                  title={a.file.name}
                >
                  {a.file.name}
                </div>
                <div
                  style={{
                    fontSize: t.font.size.xs,
                    color: t.color.textMuted,
                    display: "flex",
                    gap: t.space.s2,
                  }}
                >
                  <span>{formatFileSize(a.file.size)}</span>
                  {status && (
                    <span
                      style={{
                        color: isError
                          ? t.color.danger
                          : isReady
                          ? t.color.success
                          : t.color.accent,
                      }}
                    >
                      {status}
                    </span>
                  )}
                </div>
              </div>
              <Tooltip title="Remove">
                <button
                  type="button"
                  onClick={() => onRemove(a.id)}
                  aria-label={`Remove ${a.file.name}`}
                  style={{
                    border: "none",
                    background: "transparent",
                    cursor: "pointer",
                    color: t.color.textMuted,
                    padding: 2,
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <CloseOutlined style={{ fontSize: 12 }} />
                </button>
              </Tooltip>
            </div>
            {a.status === "uploading" && (
              <div
                style={{
                  height: 2,
                  background: t.color.border,
                  borderRadius: 2,
                  overflow: "hidden",
                  position: "relative",
                }}
              >
                <div className="askai-progress-indeterminate" />
              </div>
            )}
            {a.status === "ready" && (
              <div
                style={{
                  height: 2,
                  background: t.color.success,
                  borderRadius: 2,
                }}
              />
            )}
          </div>
        );
      })}
    </div>
  );
};

export default AttachmentChips;
